import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronRight } from "lucide-react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/classes", label: "Classes" },
  { to: "/services", label: "Services" },
  { to: "/instructors", label: "Founders & Our Team" },
  { to: "/awards", label: "Awards" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact" },
];

const LOGO_URL = "/brand/phoenix-logo.png";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 left-0 right-0 z-50 bg-[#0a0a0a] border-b border-[#ffb700]/25"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 h-20 sm:h-24 flex items-center justify-between gap-3 sm:gap-6">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 sm:gap-3 group shrink-0 min-w-0">
          <img
            src={LOGO_URL}
            alt="Phoenix Dance Academy"
            width="80"
            height="80"
            loading="eager"
            fetchpriority="high"
            decoding="async"
            className="h-14 sm:h-16 lg:h-20 w-auto object-contain transition-transform duration-300 group-hover:scale-105 shrink-0"
            style={{ minWidth: "44px" }}
          />
          <span
            className="font-display tracking-wider text-white text-[25px] leading-none sm:text-base sm:leading-tight lg:text-xl sm:whitespace-nowrap"
            style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.04em" }}
          >
            PHOENIX DANCE ACADEMY
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-3 xl:px-4 py-2 text-xs xl:text-sm uppercase tracking-wider font-medium transition-colors whitespace-nowrap ${
                  isActive ? "text-[#ffb700]" : "text-white/85 hover:text-[#ffb700]"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3 shrink-0">
          <Link to="/book-trial" className="hidden sm:block">
            <button
              data-testid="navbar-book-trial-btn"
              className="btn-phoenix bg-[#ff4500] text-white px-5 h-11 uppercase tracking-wider text-xs font-semibold inline-flex items-center gap-1 border-0 whitespace-nowrap"
            >
              Book Free Trial <ChevronRight className="w-4 h-4" strokeWidth={2} />
            </button>
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="xl:hidden w-11 h-11 flex items-center justify-center border border-white/20 text-white shrink-0"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="xl:hidden bg-[#0a0a0a] border-t border-white/10 animate-fade-in"
        >
          <div className="px-6 py-6 flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `py-3 px-2 text-base uppercase tracking-wider border-b border-white/5 ${
                    isActive ? "text-[#ffb700]" : "text-white/85"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link to="/book-trial" className="mt-4">
              <button
                data-testid="mobile-book-trial-btn"
                className="btn-phoenix w-full bg-[#ff4500] text-white h-12 uppercase tracking-wider text-sm font-semibold"
              >
                Book Free Trial
              </button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
